/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import useSnackbar from 'src/hooks/useCustomSnackbar';
import { useForm } from 'react-hook-form';
// libraries
import { Box, Button, CircularProgress } from '@mui/material';
import { isEmpty } from 'lodash';
// components
import Iconify from '../Iconify';
import { useSettingsContext } from '../settings/context';
import { Archive, FollowUp, NewAppointment, TreatmentComplete } from '../tabs';
// store
import { useSelector, dispatch } from '../../redux/store';
import { updateError } from '../../redux/slices/user';
import { saveNewLeadDetail, getAppointmentNotes, CompleteTreatment } from '../../redux/slices/lead';
import { beginArchiveLead } from '../../redux/slices/lead/coreThunks';
// constant/utils/hooks
import { useTabs } from '../../hooks';
// import { Assessments as assessments, Clinical as clinical } from '../../constants/attendedAppointMentConst';
import { getTreatmentDate } from '../../utils/multiTreatmentFlow';
import { isDisabledTabForFinishButton } from '../../utils/clinCheckUtils';
import capitalizeFirstLetter from '../../utils/capitalizedFunc';
import TCOScript from './TCOScript';
import useGuidedChecklist from '../../sections/@dashboard/leadDetail/AttendedGuidedChecklist/hooks/useGuidedChecklist';
import { hasFeatureAccess } from '../../utils/featurePermission';
import resolveLeadTreatmentPrice from '../../utils/resolveLeadTreatmentPrice';
import useReminderContent from './useReminderContent';
import ScheduleReminderTaskSection from '../../sections/@dashboard/leadDetail/ScheduleReminderTaskSection';
import useLeadReminderTasks from '../../sections/@dashboard/leadDetail/hooks/useLeadReminderTasks';
import AttendedPopupShell from './AttendedPopupShell';
import AttendedFamilyHero from './AttendedFamilyHero';
import CompleteTreatmentConfirmDialog from './CompleteTreatmentConfirmDialog';
import { deriveLeadNextActionFooter } from '../../utils/leadNextActionFooter';

// Re-export so existing imports from this module don't break during transition.
export { resolveLeadTreatmentPrice };

const ConsultationAttendedAppointment = ({ selectedTreatmentData, handleReloadLeads, handleCloseAndReload, type, inputRef, handleClose, user, startTreatment, OpenAssignTask, editDetailHandler, onOpenSidebarTab }) => {
    const { isLoader } = useSelector((state) => state.loaderReducer);
    const settings = useSettingsContext();
    const { currentTab } = useTabs('');
    const { practice } = useSelector((state) => state.practice);
    const { followLead, appointmentNotes, currentTreatmentData, treatmentLoading } = useSelector((state) => state.lead);
    const { practiceId } = useSelector((state) => state.practiceTreatmentList);
    const { enqueueSnackbar } = useSnackbar();
    const [activeTab, setActiveTab] = useState(null);
    // Decouples "Finish is satisfied" from "an outcome chip is visibly selected".
    // V2 checklist activity sets this true WITHOUT setting activeTab, so the
    // "What's next?" picker stays hidden and the yellow Reminder & Follow-up card
    // isn't auto-highlighted while the checklist is already shown. The submit path
    // treats the effective outcome as 'Reminder & Follow-up' when this is true.
    const [taskActivityDone, setTaskActivityDone] = useState(false);
    // When a guided checklist is present, the custom reminder form stays collapsed
    // behind an "Add custom reminder" trigger so it doesn't compete with the checklist.
    const [showCustomReminder, setShowCustomReminder] = useState(false);
    const [depositPendingStatus, setDepositPendingStatus] = useState(false);
    // Footer "Treatment complete" confirmation dialog — treatment-close is
    // irreversible, so the footer click opens this confirm before the POST.
    const [completeConfirmOpen, setCompleteConfirmOpen] = useState(false);
    const [completeSubmitting, setCompleteSubmitting] = useState(false);
    const followLeadFilterAttend = followLead?.lead_appointment?.filter((el) => el.status === 'ATTENDED');
    // followLead is a SHARED Redux slot (state.lead.followLead) that can still hold a
    // PREVIOUSLY-viewed lead (another open tab / prior navigation) for a moment before
    // this popup's own load resolves. Trusting it blindly makes the checklist + reminder
    // panel fetch a DIFFERENT lead's tasks — the root cause of the "wrong checklist /
    // New lead follow-up shows on an attended lead" report. Trust followLead only when
    // it matches the lead this popup was opened for (the `user` prop); otherwise fall
    // back to the prop id so we never surface another lead's tasks.
    const propLeadId = user?.lead_id || user?.id;
    const followLeadMatches = followLead?.id != null && Number(followLead.id) === Number(propLeadId);
    const leadId = followLeadMatches ? followLead.id : (propLeadId ?? followLead?.id);

    const isPaymentMandatory = practice?.preferences?.is_payment_enforced_before_treatment;

    const [assigneeSelected, setAssigneeSelected] = useState(followLead?.assignees || []);

    const { initialLoading: gcInitialLoading, templatesEmpty, templatePreviewItems } = useGuidedChecklist({
        practiceId,
        leadId,
        leadDetail: followLead,
    });
    // Gate on initialLoading (first fetch only), NOT loading, so that
    // post-action refreshes inside AttendedGuidedChecklistV2 don't unmount
    // the V2 component mid-action and discard its local state.
    //
    // Also gate on templatePreviewItems.length === 0: when the lead has NO applied
    // checklist (task-checklists API returned 0 spawned items), useGuidedChecklist
    // populates templatePreviewItems with un-applied system-template items as a
    // visual preview. Those preview items must NOT render in the consultation popup —
    // the lead-detail column's hasAttendedTask gate requires real applied items, and
    // this popup must match. templatePreviewItems is [] both when real items exist
    // (flatItems.length > 0) and when all items are done (allDoneCount > 0 path),
    // so this gate never suppresses a real applied checklist.
    const v2GuidedActive = hasFeatureAccess('tasks-v2', practiceId) && !gcInitialLoading && !templatesEmpty && templatePreviewItems.length === 0;

    // Latch that tracks whether the guided checklist (including the template-preview
    // resolution) has ever fully settled. The ready condition for CAA is stricter than
    // AA: we also require templatePreviewItems.length === 0 because after
    // gcInitialLoading resolves there is a brief window where templatePreviewItems
    // holds un-applied preview items — during that window the noTemplateConfirmed
    // debounce is running and the content is still in flux. Latching only after the
    // preview items clear means the skeleton stays visible through that window too,
    // giving one smooth reveal rather than a flash of preview → real content.
    //
    // Once latched, post-save/pause/complete refreshes that briefly revisit loading
    // states do NOT re-show the skeleton — the user is already interacting.
    const hasRevealedRef = useRef(false);
    if (!gcInitialLoading && templatePreviewItems.length === 0) hasRevealedRef.current = true;
    const isInitiallyLoading = !hasRevealedRef.current;

    // tasks-v2 practice, fetch settled, but NO applicable template → the user only
    // sees ScheduleTaskReminder. CAA is the already-attended popup (no gate), so the
    // visit is complete; Finish must NOT be blocked behind scheduling a reminder.
    const isV2NoTemplate = hasFeatureAccess('tasks-v2', practiceId) && !gcInitialLoading && templatesEmpty;

    // Effective outcome for the submit/Finish gate: an explicitly-clicked chip wins;
    // otherwise checklist activity implies the Reminder & Follow-up outcome. In the
    // no-template state above, default to 'Reminder & Follow-up' so Finish is enabled
    // immediately (completable without forcing a reminder). The WITH-template case is
    // unchanged — it still requires an explicit chip or checklist activity.
    // (CAA has no attendance gate → no attendanceRecorded term.)
    const submitOutcome = activeTab
        || (taskActivityDone ? 'Reminder & Follow-up' : null)
        || (isV2NoTemplate ? 'Reminder & Follow-up' : null);

    // V2 guided checklist is now the DEFAULT experience for all practices.
    // Escape hatches (legacy v1): ?guidedV=1 in the URL, or a practice preference
    // guided_checklist_version === 'v1'. ?guidedV=2 always forces v2.
    const guidedParam = new URLSearchParams(window.location.search).get('guidedV');
    const practiceGuidedVersion = practice?.preferences?.guided_checklist_version;
    const useV2Guided = guidedParam === '2'
        || (guidedParam !== '1' && practiceGuidedVersion !== 'v1');

    // Single derived flow step — the one source of truth for every render gate.
    // CAA is the ALREADY-ATTENDED popup: there is no attendance gate, so the lead is
    // never "booked" here and the gate is treated as always-dismissed. This makes
    // flowStep never resolve to ATTENDANCE_GATE — identical behavior to before, but
    // now expressed via the SAME derivation as AttendedAppointment so the two agree.
    const isLeadBooked = false;
    const gateDismissed = true;
    const flowStep = (() => {
        if (gcInitialLoading) return 'LOADING';
        if (hasFeatureAccess('tasks-v2', practiceId) && isLeadBooked && !gateDismissed) return 'ATTENDANCE_GATE';
        if (activeTab) return 'OUTCOME_CHOSEN';
        return 'CONTENT';
    })();

    // Debug panel toggle: on by default in local dev (import.meta.env.DEV).
    // Opt-out locally: ?attDebug=0 in the URL OR localStorage attDebug='0'.
    // In production (import.meta.env.DEV === false) only explicit opt-in activates it:
    //   ?attDebug=1 in the URL OR localStorage attDebug='1'.
    const attDebug =
        new URLSearchParams(window.location.search).get('attDebug') === '1'
        || (typeof window !== 'undefined' && window.localStorage.getItem('attDebug') === '1')
        || (import.meta.env.DEV
            && new URLSearchParams(window.location.search).get('attDebug') !== '0'
            && !(typeof window !== 'undefined' && window.localStorage.getItem('attDebug') === '0'));

    // ── Debug-only section outlines (behind attDebug; zero visual change otherwise) ──
    const dbgSx = (color) => (attDebug ? { outline: `2px dashed ${color}`, outlineOffset: '-2px', position: 'relative' } : null);
    const DbgTag = ({ color, label }) => (attDebug ? (
        <Box component="span" sx={{ position: 'absolute', top: 0, left: 0, zIndex: 50, bgcolor: color, color: '#fff', fontSize: '9px', fontWeight: 700, lineHeight: 1.4, px: 0.5, borderBottomRightRadius: 4, letterSpacing: '0.04em', pointerEvents: 'none' }}>{label}</Box>
    ) : null);
    DbgTag.propTypes = { color: PropTypes.string, label: PropTypes.string };

    useEffect(() => {
        if (type !== 'CreatedLead' && !isEmpty(currentTreatmentData)) {
            setDepositPendingStatus(currentTreatmentData.is_treatment_fee_pending);
        }
    }, [currentTreatmentData]);

    const getFieldsValue = (value) => {
        const appointmentNote = Array.isArray(appointmentNotes) ? appointmentNotes[0] : null;
        const clinicalAttachment = appointmentNote?.clinical_attachments?.[0];
        const assessmentAttachment = appointmentNote?.assessment_attachments?.[0];

        if (appointmentNote) {
            switch (value) {
                case 'is_clinical_photographs_taken':
                    return clinicalAttachment?.is_clinical_photographs_taken || false;
                case 'is_oral_health_assessment_taken':
                    return clinicalAttachment?.is_oral_health_assessment_taken || false;
                case 'is_xray_taken':
                    return clinicalAttachment?.is_xray_taken || false;
                case 'is_digital_scan_taken':
                    return assessmentAttachment?.is_digital_scan_taken || false;
                case 'is_impressions_taken':
                    return assessmentAttachment?.is_impressions_taken || false;
                case 'is_treatment_options_discussed':
                    return assessmentAttachment?.is_treatment_options_discussed || false;
                case 'is_treatment_plan_confirmed':
                    return assessmentAttachment?.is_treatment_plan_confirmed || false;
                case 'is_finance_options_discussed':
                    return assessmentAttachment?.is_finance_options_discussed || false;
                case 'notes':
                    return appointmentNote?.additional_notes || '';
                default:
                    return false;
            }
        }
        return false;
    };

    const defaultValues = {
        is_clinical_photographs_taken: getFieldsValue('is_clinical_photographs_taken') || false,
        is_oral_health_assessment_taken: getFieldsValue('is_oral_health_assessment_taken') || false,
        is_xray_taken: getFieldsValue('is_xray_taken') || false,
        is_digital_scan_taken: getFieldsValue('is_digital_scan_taken') || false,
        is_impressions_taken: getFieldsValue('is_impressions_taken') || false,
        is_treatment_options_discussed: getFieldsValue('is_treatment_options_discussed') || false,
        is_treatment_plan_confirmed: getFieldsValue('is_treatment_plan_confirmed') || false,
        is_finance_options_discussed: getFieldsValue('is_finance_options_discussed') || false,
        notes: getFieldsValue('notes') || ''
    };

    const methods = useForm({
        defaultValues,
    });

    const {
        reset,
        handleSubmit
    } = methods;

    const appointmentNotesUpdate = async (data, noteData, callAppointmentNote) => {
        // Appointment-note payload is ONLY assembled/dispatched when this lead has
        // an appointment (data = followLead.lead_appointment). The downstream
        // addAppointmentNotes thunk consumes this `attentendAppoiment` state to
        // save the clinical/assessment note against the appointment.
        if (data?.length > 0) {
            const noteDetail = {
                additional_notes: noteData.notes,
                clinical_attachments: [
                    {
                        is_clinical_photographs_taken: noteData.is_clinical_photographs_taken,
                        is_oral_health_assessment_taken: noteData.is_oral_health_assessment_taken,
                        is_xray_taken: noteData.is_xray_taken
                    }
                ],
                assessment_attachments: [
                    {
                        is_digital_scan_taken: noteData.is_digital_scan_taken,
                        is_impressions_taken: noteData.is_impressions_taken,
                        is_treatment_options_discussed: noteData.is_treatment_options_discussed,
                        is_treatment_plan_confirmed: noteData.is_treatment_plan_confirmed,
                        is_finance_options_discussed: noteData.is_finance_options_discussed
                    }
                ]
            };
            dispatch(saveNewLeadDetail({ attentendAppoiment: { noteDetail, activeTab: submitOutcome, callAppointmentNote } }));
        }
        // ALWAYS complete/close — this must run whether or not an appointment exists,
        // otherwise Finish is a silent no-op on leads with no lead_appointment.
        // submit('finish') routes to the active outcome's close path (FollowUp →
        // addNotes(...handleClose)/updateCompleteStatus; NewAppointment/Archive/
        // TreatmentComplete → their own finish handlers). When the active outcome
        // tab exposes no imperative submit (e.g. the V2 guided checklist renders
        // instead of the V1 FollowUp ref), fall back to handleClose so the popup
        // still closes instead of dying silently.
        if (typeof inputRef?.current?.submit === 'function') {
            await inputRef.current.submit('finish');
        } else {
            handleClose();
        }
    };

    const onSubmit = async (data) => {
        // Gate on submitOutcome (not just activeTab/taskActivityDone): an explicit
        // chip, checklist activity, OR the no-template attended state all yield a
        // non-null submitOutcome and should be allowed to complete.
        if (!submitOutcome) {
            dispatch(updateError({ message: 'Please select any one Tab', success: false }));
            return;
        }
        const callAppointmentNote = JSON.stringify(data) !== JSON.stringify(defaultValues);
        appointmentNotesUpdate(followLead?.lead_appointment, data, callAppointmentNote);
    };

    const handleNext = async () => {
        // V2 guided-checklist "Reminder & Follow-up" outcome (both the
        // with-template case → v2GuidedActive, and the no-template case →
        // isV2NoTemplate showing ScheduleTaskReminder) persists its tasks +
        // reminders INLINE as the user does them. There is no submittable form
        // ref on this outcome (the V1 FollowUp ref only exists when tasks-v2 is
        // OFF), so the form-submit path — handleSubmit(onSubmit) →
        // appointmentNotesUpdate → inputRef.current.submit('finish') → FollowUp's
        // Yup-validated handleSubmit — never closes the popup. Finish here only
        // needs to close + reload the leads table so the lead leaves the queue.
        const isV2ReminderOutcome =
            submitOutcome === 'Reminder & Follow-up' && (v2GuidedActive || isV2NoTemplate);
        if (isV2ReminderOutcome) {
            if (typeof handleCloseAndReload === 'function') handleCloseAndReload();
            else {
                if (typeof handleReloadLeads === 'function') handleReloadLeads();
                handleClose();
            }
            return;
        }
        handleSubmit(onSubmit)();
    };

    // Footer "Treatment complete" → open the irreversible-close confirmation dialog.
    // The actual POST happens in handleConfirmTreatmentComplete (the dialog's
    // Confirm button), NOT on this click — treatment-close can't be undone.
    const handleTreatmentComplete = () => {
        setCompleteConfirmOpen(true);
    };

    // Confirm → close the treatment via the REAL CompleteTreatment thunk (the same
    // one the treatment-started hub uses; POST
    // /leads/{lid}/treatments/{ltid}/treatment_closed/) and then close + reload.
    //
    // We do NOT route through the TreatmentComplete tab's submit('finish') path:
    // that only dispatches updateCompleteStatus(true) and relies on the
    // ConsultationAttended host listener, which has NO treatment-complete branch
    // (only noteDetail + addReminder) — so for a bare treatment-complete outcome it
    // never POSTs nor closes (the dead path the footer move exposed). Dispatching
    // CompleteTreatment directly is the working path.
    const handleConfirmTreatmentComplete = () => {
        const completeLeadId = followLead?.id || user?.lead_id || user?.id;
        const treatmentId =
            selectedTreatmentData?.treatmentData?.id
            ?? followLead?.lead_treatments?.[0]?.id
            ?? null;
        if (!completeLeadId || !treatmentId) {
            dispatch(updateError({ success: false, message: 'Could not resolve the treatment to complete.' }));
            setCompleteConfirmOpen(false);
            return;
        }
        setCompleteSubmitting(true);
        const price = resolveLeadTreatmentPrice({ followLead, selectedTreatmentData });
        const data = {
            treatment: getSelectedTreatment() || '',
            custom_price: price ?? '',
        };
        // onClose runs on a successful treatment_closed POST → close + reload so the
        // lead leaves the started queue. CompleteTreatment calls this as
        // handleClose('finish', leadId); ignore the args and do close+reload.
        // (The thunk also clears its own loaders + shows a success toast on 200; on
        // failure it shows an error toast and never calls this, so we re-enable below.)
        const onClose = () => {
            setCompleteSubmitting(false);
            setCompleteConfirmOpen(false);
            if (typeof handleCloseAndReload === 'function') handleCloseAndReload();
            else {
                if (typeof handleReloadLeads === 'function') handleReloadLeads();
                handleClose();
            }
        };
        dispatch(CompleteTreatment(data, completeLeadId, treatmentId, practiceId, onClose));
    };

    // Reminder & Follow-up chip content.
    //
    // V2 practices with a template applied → show the V2 guided checklist
    //   (AttendedGuidedChecklistV2 / AttendedGuidedChecklist) for the task-display
    //   portion; ScheduleTaskReminder is appended so users can also create a
    //   standalone follow-up task alongside the checklist actions.
    //
    // V2 practices with NO template applied (v2GuidedActive is false because
    //   templatesEmpty) → show ScheduleTaskReminder directly (fixes the
    //   "blank Reminder & Follow-up tab" for practices with tasks-v2 but
    //   no checklist template, e.g. lead 152413).
    //
    // Non-V2 practices → unchanged V1 FollowUp fallback.
    // Checklist activity satisfies Finish WITHOUT revealing the picker or
    // highlighting the yellow Reminder & Follow-up card: set a flag only,
    // never setActiveTab (which would un-hide the picker via its !activeTab gate).
    const onTaskActivity = () => {
        setTaskActivityDone(true);
    };

    // Reminder-component consolidation (Phase 2): when tasks-v2 is active with an
    // applied template, render the SAME shared <ScheduleReminderTaskSection> the
    // FollowUpV2 popup uses, so this popup gains standalone/ad-hoc tasks, the
    // pause/complete affordances, the V2 action buttons and the creation/completion
    // animations — instead of the template-only AttendedGuidedChecklistV2 path.
    //
    // The hook is invoked unconditionally (hooks can't be conditional). It wraps
    // useV2ChecklistAutocomplete keyed by practiceId:leadId — the SAME module-level
    // store useGuidedChecklist subscribes to above — so this adds no extra fetch and
    // the section + header badge stay in sync off one source of truth.
    // Same stale-followLead guard as `leadId` above — the reminder panel must never
    // fetch another lead's open tasks (see the comment at the leadId definition).
    const reminderLeadId = leadId;
    const sharedChecklist = useLeadReminderTasks({ practiceId, leadId: reminderLeadId });

    // Bridge: the shared section drives task activity itself, but CAA's Finish gate
    // keys off taskActivityDone. Mark it done as soon as the lead has any task so
    // Finish is enabled (matches the previous onTaskActivity semantics — any
    // checklist interaction satisfied Finish). v2GuidedActive already implies an
    // applied template, so there is real activity to gate on.
    useEffect(() => {
        if (v2GuidedActive && (sharedChecklist.items?.length ?? 0) > 0) {
            setTaskActivityDone(true);
        }
    }, [v2GuidedActive, sharedChecklist.items]);

    // Phase 2: the previously-inline reminderTabComponent IIFE lives in the shared
    // useReminderContent hook. CAA passes mode 'post-attended', the V1 FollowUp
    // fallback node, and NO gate props (the attendance gate is AA-only). This is
    // used for the non-v2 and no-template branches; the v2-with-template branch is
    // swapped to ScheduleReminderTaskSection below.
    const legacyReminderTabComponent = useReminderContent({
        practiceId,
        leadId: reminderLeadId,
        followLead,
        v2GuidedActive,
        useV2Guided,
        showCustomReminder,
        setShowCustomReminder,
        onTaskActivity,
        handleClose,
        handleReloadLeads,
        onOpenSidebarTab,
        // Active treatment being acted on — scopes custom-reminder create + pause.
        leadTreatmentId: selectedTreatmentData?.treatmentData?.id
            ?? followLead?.lead_treatments?.[0]?.id
            ?? null,
        mode: 'post-attended',
        V1Fallback: <FollowUp ref={inputRef} handleClose={handleClose} type="ConsultationAttended" />,
        dbgSx,
        DbgTag,
    });

    const handleExplicitArchiveSubmit = async ({ data, openCommunication }) => {
        const archiveLeadId = followLead?.id || user?.lead_id || user?.id;
        const archiveTreatmentId = selectedTreatmentData?.treatmentData?.id
            ?? followLead?.lead_treatments?.[0]?.id
            ?? null;
        if (!archiveLeadId || !practiceId) {
            dispatch(updateError({ success: false, message: 'Cannot archive: lead not identified. Please close and reopen.' }));
            return;
        }
        await dispatch(beginArchiveLead({
            leadId: archiveLeadId,
            practiceId,
            leadTreatmentId: archiveTreatmentId,
            reason: data?.archiveReason || '',
            note: data?.archiveNotes || '',
            isOpenForCommunication: openCommunication?.is_open_for_communication,
            source: 'consultation-attended-popup:archive',
            handleClose,
        }));
        handleReloadLeads?.();
    };

    // #3 flash guard (this popup only). The V2 checklist decision settles in two
    // phases: useGuidedChecklist briefly reports templatesEmpty=true (no applied
    // template yet) before the template resolves and v2GuidedActive flips true.
    // That transient renders the no-template "schedule a reminder"
    // (ScheduleTaskReminder) branch for a frame before the real (often empty)
    // checklist — the visible flash. Debounce ONLY the no-template branch: hold a
    // small loader during the ambiguous window. v2GuidedActive (the real checklist)
    // still renders immediately, and a genuine no-template practice still shows
    // ScheduleTaskReminder once the window confirms it. No shell/footer/routing change.
    const [noTemplateConfirmed, setNoTemplateConfirmed] = useState(false);
    useEffect(() => {
        if (v2GuidedActive || gcInitialLoading) {
            setNoTemplateConfirmed(false);
            return undefined;
        }
        const id = setTimeout(() => setNoTemplateConfirmed(true), 250);
        return () => clearTimeout(id);
    }, [v2GuidedActive, gcInitialLoading]);
    const reminderDecisionPending = !gcInitialLoading && !v2GuidedActive && !noTemplateConfirmed;

    const v2ReminderNode = (
        <ScheduleReminderTaskSection
            key={reminderLeadId}
            practiceId={practiceId}
            leadId={reminderLeadId}
            leadDetail={followLead}
            leadTreatmentId={selectedTreatmentData?.treatmentData?.id ?? followLead?.lead_treatments?.[0]?.id ?? null}
            onOpenSidebarTab={onOpenSidebarTab}
            onReloadLeads={handleReloadLeads}
            onClose={handleClose}
            checklist={sharedChecklist}
            renderHero
            debugSource="attended-consultation"
            dbgSx={dbgSx}
            DbgTag={DbgTag}
        />
    );

    let reminderTabComponent;
    if (reminderDecisionPending) {
        reminderTabComponent = (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
                <CircularProgress size={24} />
            </Box>
        );
    } else if (v2GuidedActive) {
        reminderTabComponent = v2ReminderNode;
    } else {
        reminderTabComponent = legacyReminderTabComponent;
    }

    // Treatment label shown on the "Starting treatment" chip and "Treatment complete"
    // footer button when the lead has more than one treatment.
    //
    // For CAA the target treatment is always selectedTreatmentData.treatmentData
    // (the specific treatment the rep clicked). We count treatments from
    // user.treatmentData first (set synchronously from leadDetail.lead_treatments
    // in setLeadData) so the label is available before followUpSlice resolves.
    // Only non-null when >1 treatment — single-treatment leads show no label.
    //
    // MUST be declared before PROFILE_TABS (which references it at the
    // "Starting treatment" chip label) to avoid a TDZ ReferenceError.
    const allLeadTreatmentsForLabel = user?.treatmentData || followLead?.lead_treatments || [];
    const actionTreatmentLabel = (() => {
        if (allLeadTreatmentsForLabel.length <= 1) return null;
        const targetTx = selectedTreatmentData?.treatmentData;
        return targetTx?.treatment_data?.name
            || targetTx?.treatment_data?.treatment_item?.name
            || targetTx?.treatment
            || null;
    })();

    const PROFILE_TABS = [
        {
            // Leftmost chip — opens the V2 Guided Checklist (or V1 FollowUp fallback when
            // the tasks-v2 feature flag is off / templates are empty).
            //
            // `value` is the internal identifier the AttendedPopupShell gates on
            // (AttendedPopupShell.jsx:195/203 hardcode this string to swap the
            // OUTCOME box for the reminderNode). DO NOT rename.
            //
            // `label` is the user-facing chip text — renamed to 'Active tasks'
            // 2026-06-07 per Raj. FollowUpV2.jsx mirrors the same rename so both
            // popups display identical chip text. AttendedPopupShell renders
            // `tab.label || tab.value` so callers without a label still work.
            value: 'Reminder & Follow-up',
            label: 'Active tasks',
            icon: <Iconify icon={'solar:bell-bold-duotone'} width={20} height={20} />,
            component: reminderTabComponent,
        },
        {
            value: 'Starting treatment',
            // When the lead has >1 treatment, append the target treatment name so
            // reps can see which treatment the action will actually operate on.
            label: actionTreatmentLabel ? `Starting treatment · ${actionTreatmentLabel}` : undefined,
            icon: <Iconify icon={'solar:medical-kit-bold-duotone'} width={20} height={20} />,
            component: <NewAppointment selectedTreatmentData={selectedTreatmentData} ref={inputRef} type={type} rowId={user.lead_id} handleClose={handleClose} startTreatment={startTreatment} handleReloadLeads={handleReloadLeads} />,
        },
        {
            value: 'Archive',
            icon: <Iconify icon={'eva:archive-fill'} width={20} height={20} />,
            component: <Archive ref={inputRef} handleClose={handleClose} handleReloadLeads={handleReloadLeads} onExplicitArchiveSubmit={handleExplicitArchiveSubmit} />,
        }
    ];

    // 'Treatment complete' is footer-only (not a picker chip) — it's mounted in the
    // shell's OUTCOME box via hiddenTabs when the footer button selects it, so its
    // submit ref (inputRef) populates before handleTreatmentComplete fires.
    const TREATMENT_COMPLETE_TAB = {
        value: 'Treatment complete',
        icon: <Iconify icon={'akar-icons:circle-check-fill'} width={20} height={20} />,
        component: <TreatmentComplete ref={inputRef} type={type} handleNext={handleNext} handleClose={handleClose} />,
    };

    const getAppointmentId = () => {
        if (!isEmpty(selectedTreatmentData)) {
            return followLead?.lead_appointment?.find((ele) => ele?.lead_treatment === selectedTreatmentData?.treatmentData?.id)?.id || followLead.lead_appointment.filter((el) => el.status === 'ATTENDED' || el.status === 'CONSULTATION_ATTENDED')?.[0].id;
        }
        return followLead.lead_appointment.filter((el) => el.status === 'ATTENDED' || el.status === 'CONSULTATION_ATTENDED')?.[0].id;
    };

    useEffect(() => {
        if (followLead && followLead.lead_appointment && followLead.lead_appointment.length > 0) {
            const appointmentID = followLead.lead_appointment.filter((el) => el.status === 'ATTENDED' || el.status === 'CONSULTATION_ATTENDED');
            if (appointmentID.length > 0) {
                dispatch(getAppointmentNotes(practiceId, user?.lead_id, getAppointmentId(), handleSuccess));
            }
        }
    }, [followLead]);

    useEffect(() => {
        reset(defaultValues);
    }, [appointmentNotes]);

    const handleSuccess = (data) => {
        enqueueSnackbar(data.message || '', {
            variant: data.success ? 'success' : 'error',
            anchorOrigin: {
                vertical: 'top',
                horizontal: 'center'
            }
        });
    };

    useEffect(() => {
        if (startTreatment === 'StartTreatment') {
            setActiveTab('Starting treatment');
        }
    }, [startTreatment]);

    // This popup is for already-attended consultations (no attendance gate).
    // Default the outcome to Reminder & Follow-up once V2 settles so the
    // simplified picker reflects the in-progress reminder flow. One-shot guard
    // so we don't fight a user who manually picks a different outcome.
    // Keys on v2GuidedActive (single source) — its dep ensures this re-runs once
    // the guided-checklist fetch resolves (!gcInitialLoading && !templatesEmpty).
    const defaultedToReminderRef = useRef(false);
    useEffect(() => {
        if (
            !defaultedToReminderRef.current
            && hasFeatureAccess('tasks-v2', practiceId)
            && v2GuidedActive
            && !activeTab
        ) {
            setActiveTab('Reminder & Follow-up');
            defaultedToReminderRef.current = true;
        }
    }, [v2GuidedActive, activeTab, practiceId]);

    const getSelectedTreatment = () => selectedTreatmentData?.treatmentData?.treatment || followLead?.lead_treatments?.[0]?.treatment;

    const TABS = PROFILE_TABS;

    const getSelectedTreatmentDateTime = () => {
        const tempDate = followLead?.lead_appointment?.find((ele) => ele?.lead_treatment === selectedTreatmentData?.treatmentData?.id)?.datetime;
        if (!isEmpty(selectedTreatmentData) && tempDate) return getTreatmentDate(tempDate) || '';
        return getTreatmentDate(followLeadFilterAttend?.[0]?.datetime) || '';
    };

    const getCurrentTreatmentAssignees = () => {
        if (!isEmpty(selectedTreatmentData)) {
            const found = followLead?.lead_appointment?.find((ele) => ele?.lead_treatment === selectedTreatmentData?.treatmentData?.id);
            return found?.assignees || followLeadFilterAttend?.[0]?.assignees;
        }
        return followLeadFilterAttend?.[0]?.assignees;
    };

    useEffect(() => {
        if (followLead?.assignees) {
            setAssigneeSelected(followLead.assignees || []);
        }
    }, [followLead]);

    // Header slot — uses shared AttendedFamilyHero component.
    // CAA-specific divergence from V2:
    //   • statusLabel="Attended" (fixed), statusColor="#8b5cf6" (purple)
    //   • statusIcon="mdi:calendar-check"
    //   • onEdit → editDetailHandler (edit-pencil top-right)
    //   • onAssignPractitioner → OpenAssignTask (pencil in expanded Practitioner row)
    const headerNode = (user !== undefined && user && (
        <AttendedFamilyHero
            followLead={followLead}
            leadId={leadId}
            leadName={`${capitalizeFirstLetter(followLead?.first_name || '')} ${capitalizeFirstLetter(followLead?.last_name || '')}`}
            treatment={getSelectedTreatment() || '—'}
            treatmentDateTime={getSelectedTreatmentDateTime() || null}
            treatmentAssignees={getCurrentTreatmentAssignees() || []}
            assigneeSelected={assigneeSelected}
            setAssigneeSelected={setAssigneeSelected}
            price={resolveLeadTreatmentPrice({ followLead, selectedTreatmentData })}
            statusLabel="Attended"
            statusColor="#8b5cf6"
            statusIcon="mdi:calendar-check"
            onEdit={() => editDetailHandler()}
            onAssignPractitioner={(e) => OpenAssignTask(user, getCurrentTreatmentAssignees(), e)}
            dbgSx={dbgSx}
            DbgTag={DbgTag}
        />
    ));

    // CAA has no attendance gate (isLeadBooked=false, gateDismissed=true) → never
    // ATTENDANCE_GATE, so no gate slot.
    const gateNode = null;

    // OUTCOME box — CAA's "Back to checklist" affordance (shown when V2 is active).
    const outcomeBackToChecklistNode = v2GuidedActive ? (
        <Button
            onClick={() => setActiveTab(null)}
            startIcon={<Iconify icon="eva:arrow-back-fill" width={14} height={14} />}
            sx={{
                color: '#0071e3',
                fontSize: '12px',
                fontWeight: 500,
                textTransform: 'none',
                letterSpacing: '-0.005em',
                px: 1,
                py: 0.25,
                mb: 1.5,
                ml: -1,
                borderRadius: '6px',
                minWidth: 'auto',
                '&:hover': { bgcolor: 'rgba(0, 113, 227, 0.06)' },
            }}
        >
            Back to checklist
        </Button>
    ) : null;

    const activeReminderTreatmentId = selectedTreatmentData?.treatmentData?.id
        ?? followLead?.lead_treatments?.[0]?.id
        ?? null;
    const leadNextActionFooter = deriveLeadNextActionFooter({
        items: sharedChecklist.items,
        activeTreatmentId: activeReminderTreatmentId,
        isActive: v2GuidedActive,
    });

    // Finish disabled — CAA's own args (no attendanceRecorded term).
    // ClinCheck-status arg is now null — the ClinCheck outcome tab was removed,
    // so its status branch in isDisabledTabForFinishButton is unreachable.
    const finishDisabled = isDisabledTabForFinishButton(null, currentTab, submitOutcome, isPaymentMandatory, depositPendingStatus, type, treatmentLoading) || isLoader;

    const debugStateObject = {
        component: 'ConsultationAttendedAppointment',
        practiceId,
        leadId,
        tasksV2Feature: hasFeatureAccess('tasks-v2', practiceId),
        gcInitialLoading,
        templatesEmpty,
        v2GuidedActive,
        guidedParam,
        useV2Guided,
        flowStep,
        activeTab,
        submitOutcome,
        taskActivityDone,
    };

    return (
        <>
            <AttendedPopupShell
                practiceId={practiceId}
                flowStep={flowStep}
                activeTab={activeTab}
                onActiveTabChange={setActiveTab}
                tabs={TABS}
                hiddenTabs={[TREATMENT_COMPLETE_TAB]}
                onTreatmentComplete={handleTreatmentComplete}
                treatmentCompleteLoading={isLoader || completeSubmitting}
                showTreatmentCompleteButton={flowStep !== 'ATTENDANCE_GATE'}
                showSimilarLeadWarning
                tcoNode={<TCOScript handleClose={handleClose} handleNext={onSubmit} ref={inputRef} />}
                methods={methods}
                onFormSubmit={handleSubmit(onSubmit)}
                headerNode={headerNode}
                gateNode={gateNode}
                reminderNode={reminderTabComponent}
                outcomeBackToChecklist={outcomeBackToChecklistNode}
                onFinish={() => handleNext()}
                finishLoading={isLoader}
                finishDisabled={finishDisabled}
                finishClassName="finish-button"
                finishLabel={leadNextActionFooter.label || 'Finish'}
                finishHelperText={leadNextActionFooter.helper}
                isFinishAvailable
                isInitiallyLoading={isInitiallyLoading}
                themeStretch={settings.themeStretch}
                handleClose={handleClose}
                actionTreatmentLabel={actionTreatmentLabel}
                debugStateObject={debugStateObject}
                attDebug={attDebug}
                dbgSx={dbgSx}
                DbgTag={DbgTag}
            />
            <CompleteTreatmentConfirmDialog
                open={completeConfirmOpen}
                submitting={completeSubmitting}
                onCancel={() => { setCompleteConfirmOpen(false); setCompleteSubmitting(false); }}
                onConfirm={handleConfirmTreatmentComplete}
            />
        </>
    );
};


ConsultationAttendedAppointment.propTypes = {
    handleReloadLeads: PropTypes.func,
    handleCloseAndReload: PropTypes.func,
    type: PropTypes.string,
    inputRef: PropTypes.object,
    handleClose: PropTypes.func,
    user: PropTypes.object,
    startTreatment: PropTypes.string,
    OpenAssignTask: PropTypes.func,
    editDetailHandler: PropTypes.func,
    selectedTreatmentData: PropTypes.func,
    /** Called with 'email' or 'whatsapp' to open the matching right-sidebar tab. */
    onOpenSidebarTab: PropTypes.func,
};

export default ConsultationAttendedAppointment;
